/**
 * Unified checkout gating: zero-price cart lines vs. AGB acceptance.
 * Exposed as window.JmCartCheckoutGate for cart.js (classic) and cart-drawer.js (module).
 */
(function () {
  function getCookie(name) {
    try {
      const match = document.cookie.match(
        new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)")
      );
      return match ? decodeURIComponent(match[1]) : "";
    } catch (_) {
      return "";
    }
  }

  function termsAccepted() {
    if (getCookie("term_conditions")) return true;
    const boxes = document.querySelectorAll(".conditions_form_minicart");
    for (let i = 0; i < boxes.length; i++) {
      if (boxes[i].checked) return true;
    }
    return false;
  }

  function hasZeroPriceItems(parsedState) {
    if (!window.themeGlobalVariables?.settings?.jm_block_zero_price) return false;
    const exemptIds = new Set();
    document
      .querySelectorAll(".jm-zero-price-cart-warning[data-zp-exempt-ids]")
      .forEach((el) => {
        (el.dataset.zpExemptIds || "").split(",").forEach((id) => {
          if (id) exemptIds.add(Number(id));
        });
      });
    if (parsedState?.items?.length) {
      return parsedState.items.some((item) => {
        return (
          (item.original_price ?? item.price) <= 0 &&
          !exemptIds.has(item.product_id)
        );
      });
    }
    const warn = document.querySelector(".jm-zero-price-cart-warning");
    return warn && warn.style.display !== "none" && warn.offsetParent !== null;
  }

  function findTermsCheckbox(btn) {
    const ctas = btn?.closest(".cart__ctas, .drawer-bottom");
    return (
      ctas?.querySelector(".conditions_form_minicart") ||
      document.querySelector(".conditions_form_minicart")
    );
  }

  function updateCheckoutLabel(btn, isAccepted) {
    const span = btn.querySelector(".jm-checkout-label");
    if (!span) return;
    const ready = btn.dataset.labelCheckout;
    const pending = btn.dataset.labelTerms;
    if (!ready || !pending) return;
    span.textContent = isAccepted ? ready : pending;
  }

  function nudgeTerms(fromEl) {
    const scope = fromEl.closest(".cart__ctas, .drawer-bottom") || document;
    const terms =
      scope.querySelector("terms-conditions") ||
      document.querySelector("terms-conditions");
    if (terms && typeof terms.showTermsMessage === "function") {
      terms.showTermsMessage();
      if (typeof terms.focusConditionsCheckbox === "function") {
        terms.focusConditionsCheckbox();
      }
      return;
    }
    // Fallback when the custom element has not upgraded yet.
    const warn =
      scope.querySelector(".jm-terms-required-warning") ||
      document.querySelector(".jm-terms-required-warning");
    if (warn) {
      warn.removeAttribute("hidden");
      try {
        warn.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } catch (_) {
        /* fail-open */
      }
    }
  }

  // Express buttons (Shop Pay/PayPal/Apple Pay) render inside iframes, so a
  // plain pointer-events:none leaves shoppers tapping a dead button with no
  // feedback. Mirror the PDP pattern: a transparent overlay captures the tap
  // and surfaces the AGB hint instead of silently swallowing it. Own class
  // (not the PDP's .jm-dynamic-checkout-terms-blocker) so future PDP CSS
  // changes cannot silently restyle this overlay; all styling is inline.
  function ensureExpressBlocker(wrapper) {
    let blocker = wrapper.querySelector(".jm-cart-express-terms-blocker");
    if (!blocker) {
      blocker = document.createElement("div");
      blocker.className = "jm-cart-express-terms-blocker";
      blocker.setAttribute("role", "presentation");
      blocker.style.position = "absolute";
      blocker.style.inset = "0";
      blocker.style.zIndex = "5";
      blocker.style.cursor = "not-allowed";
      blocker.style.background = "transparent";
      blocker.style.display = "none";
      blocker.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        nudgeTerms(wrapper);
      });
      wrapper.classList.add("relative");
      wrapper.appendChild(blocker);
    }
    return blocker;
  }

  function syncCheckoutButtons(parsedState) {
    const zp = parsedState ? hasZeroPriceItems(parsedState) : hasZeroPriceItems();
    const accepted = termsAccepted();
    const needsTerms = document.querySelector(".jm-needs-terms") != null;

    document.querySelectorAll(".jm-zero-price-cart-warning").forEach((el) => {
      el.style.display = zp ? "" : "none";
    });
    document.querySelectorAll(".cart__dynamic-checkout-buttons").forEach((el) => {
      el.style.pointerEvents = "";
      el.removeAttribute("aria-hidden");
      if (zp) {
        el.style.display = "none";
        return;
      }
      el.style.display = "";
      const blockExpress = needsTerms && !accepted;
      const blocker = ensureExpressBlocker(el);
      blocker.style.display = blockExpress ? "block" : "none";
      el.style.opacity = blockExpress ? "0.55" : "";
      el.setAttribute("aria-disabled", blockExpress ? "true" : "false");
    });

    document.querySelectorAll('button[name="checkout"]').forEach((btn) => {
      if (zp) {
        btn.setAttribute("data-zp-blocked", "true");
        btn.setAttribute("disabled", "");
        btn.removeAttribute("aria-disabled");
        return;
      }
      btn.removeAttribute("data-zp-blocked");
      btn.removeAttribute("disabled");
      if (!needsTerms) return;
      const cb = findTermsCheckbox(btn);
      const ok = cb ? cb.checked : accepted;
      if (ok) {
        btn.removeAttribute("aria-disabled");
        updateCheckoutLabel(btn, true);
      } else {
        btn.setAttribute("aria-disabled", "true");
        updateCheckoutLabel(btn, false);
      }
    });
  }

  function initTermsElements() {
    document.querySelectorAll("terms-conditions").forEach((el) => {
      if (typeof el.init === "function" && el.dataset.jmGateRebound !== "true") {
        try {
          el.init();
          el.dataset.jmGateRebound = "true";
        } catch (_) {
          /* ignore */
        }
      }
    });
    syncCheckoutButtons();
  }

  document.addEventListener("theme:cart-updated", (e) => {
    syncCheckoutButtons(e.detail?.cart);
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => syncCheckoutButtons());
  } else {
    syncCheckoutButtons();
  }

  window.JmCartCheckoutGate = {
    syncAll: syncCheckoutButtons,
    initTermsElements,
    termsAccepted,
    hasZeroPriceItems,
  };
})();
